import React, { useState } from "react";

interface HomeworkDetailModalProps {
  homework?: any;
}

const HomeworkDetailModal: React.FC<HomeworkDetailModalProps> = ({
  homework,
}) => {
  const [submissionFile, setSubmissionFile] = useState<File | null>(null);
  const [submissionNote, setSubmissionNote] = useState("");

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSubmissionFile(e.target.files[0]);
    }
  };

  const handleSubmitHomework = () => {
    // TODO: Submit homework API call
    console.log("Submitting homework:", {
      homeworkId: homework?.id,
      file: submissionFile,
      note: submissionNote,
    });
  };

  if (!homework) return null;

  return (
    <div className="modal fade" id="homeworkDetailModal">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Homework Details</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="modal-body">
            {/* Homework Info */}
            <div className="row mb-4">
              <div className="col-md-8">
                <h5>{homework.title}</h5>
                <p className="text-muted mb-2">
                  {homework.subject} • {homework.teacher}
                </p>
                <p>{homework.description}</p>
              </div>
              <div className="col-md-4 text-end">
                <span
                  className={`badge ${
                    homework.status === "Submitted"
                      ? "badge-soft-success"
                      : homework.status === "Overdue"
                      ? "badge-soft-danger"
                      : "badge-soft-warning"
                  } mb-2`}
                >
                  {homework.status}
                </span>
                <p className="mb-1">
                  <strong>Due:</strong> {homework.dueDate}
                </p>
                <p className="mb-1">
                  <strong>Assigned:</strong> {homework.assignedDate}
                </p>
              </div>
            </div>

            {/* Assignment Details */}
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="card-title mb-0">Assignment Instructions</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <p>
                      <strong>Max Points:</strong> {homework.maxPoints || "100"}
                    </p>
                    <p>
                      <strong>Format:</strong>{" "}
                      {homework.format || "PDF, DOC, DOCX"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p>
                      <strong>Estimated Time:</strong>{" "}
                      {homework.estimatedTime || "2 hours"}
                    </p>
                    <p>
                      <strong>Allow Late:</strong>{" "}
                      {homework.allowLate ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
                {homework.attachments && homework.attachments.length > 0 && (
                  <div className="mt-3">
                    <h6>Assignment Files:</h6>
                    {homework.attachments.map((file: any, index: number) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2"
                      >
                        <i className="ti ti-file-text me-2"></i>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {file.name}
                        </a>
                        <span className="text-muted ms-2">({file.size})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submission Section */}
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">My Submission</h6>
              </div>
              <div className="card-body">
                {homework.status === "Submitted" ? (
                  <div className="alert alert-success">
                    <div className="d-flex align-items-center">
                      <i className="ti ti-check-circle me-2"></i>
                      <div>
                        <strong>Submitted on:</strong> {homework.submittedDate}
                        {homework.grade && (
                          <div className="mt-1">
                            <strong>Grade:</strong>
                            <span
                              className={`badge ms-2 ${
                                Number(homework.grade) >= 90
                                  ? "badge-soft-success"
                                  : Number(homework.grade) >= 80
                                  ? "badge-soft-primary"
                                  : Number(homework.grade) >= 70
                                  ? "badge-soft-warning"
                                  : "badge-soft-danger"
                              }`}
                            >
                              {homework.grade}/100
                            </span>
                          </div>
                        )}
                        {homework.feedback && (
                          <div className="mt-2">
                            <strong>Teacher Feedback:</strong>
                            <p className="mb-0 mt-1">{homework.feedback}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <label className="form-label">Upload Your Work</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.png"
                        onChange={handleFileUpload}
                      />
                      <small className="text-muted">
                        Accepted formats: PDF, DOC, DOCX, TXT, JPG, PNG (Max
                        10MB)
                      </small>
                      {submissionFile && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <i className="ti ti-file me-2"></i>
                          {submissionFile.name}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => setSubmissionFile(null)}
                          >
                            <i className="ti ti-x"></i>
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Submission Notes (Optional)
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Any notes about your submission..."
                        value={submissionNote}
                        onChange={(e) => setSubmissionNote(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Close
            </button>
            {homework.status !== "Submitted" && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmitHomework}
                disabled={!submissionFile}
              >
                <i className="ti ti-upload me-2"></i>Submit Homework
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeworkDetailModal;
